import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Check, TrendingUp, Zap, Globe, Bookmark, ArrowRight, Wand2 } from "lucide-react";
import { StoreBadges } from "@/components/StoreBadges";

const DEMOS = [
  {
    idea: "AI-powered gym clothing brand",
    names: [
      { name: "NOVARA", value: "$150–$300", avail: true },
      { name: "VYNTRO", value: "$200–$450", avail: true },
      { name: "KORVA", value: "$120–$280", avail: true },
      { name: "NEXORA", value: null, avail: false },
    ],
  },
  {
    idea: "Premium coffee subscription startup",
    names: [
      { name: "BREWMOTIVE", value: "$200–$700", avail: true },
      { name: "CREMACORE", value: "$150–$500", avail: true },
      { name: "ORIGINPACE", value: "$75–$250", avail: true },
      { name: "ROASTLY", value: null, avail: false },
    ],
  },
  {
    idea: "Friendly local dog-walking app",
    names: [
      { name: "PAWVELO", value: "$100–$350", avail: true },
      { name: "TROTLY", value: "$120–$300", avail: true },
      { name: "WAGARO", value: "$90–$240", avail: true },
      { name: "FETCHR", value: null, avail: false },
    ],
  },
];

function HeroDemo() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % DEMOS.length), 4600);
    return () => clearInterval(t);
  }, []);
  const demo = DEMOS[i];
  return (
    <div className="card-soft p-5 sm:p-6 w-full max-w-[400px] shadow-card" data-testid="hero-demo">
      <div className="flex items-center gap-2 text-[12px] font-semibold text-ink-subtle mb-3">
        <Wand2 size={14} className="text-brand" /> Your business
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={demo.idea}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.4 }}
          className="rounded-lg bg-canvas px-3.5 py-3 text-[14px] font-medium text-ink"
        >
          {demo.idea}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center gap-2 text-[12px] font-semibold text-ink-subtle mt-4 mb-2.5">
        <Sparkles size={14} className="text-brand" /> Generated
      </div>
      <div className="space-y-2">
        <AnimatePresence mode="popLayout">
          {demo.names.map((n, idx) => (
            <motion.div
              key={demo.idea + n.name}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ delay: idx * 0.12, duration: 0.35 }}
              className={`flex items-center justify-between rounded-lg border hairline px-3.5 py-2.5 ${
                n.avail ? "bg-surface" : "bg-canvas-alt opacity-60"
              }`}
            >
              <span className="font-display font-extrabold tracking-[-0.01em] text-ink text-[15px]">{n.name}</span>
              <div className="flex items-center gap-2">
                {n.avail ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-success bg-success-bg rounded-full px-2 py-0.5">
                    <Check size={11} strokeWidth={3} /> .com
                  </span>
                ) : (
                  <span className="text-[11px] font-semibold text-ink-subtle">taken</span>
                )}
                {n.value && <span className="text-[11px] font-bold text-ink-muted">{n.value}</span>}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

const STEPS = [
  { icon: Wand2, title: "Describe your idea", text: "Tell Namable what you're building in a sentence or two." },
  { icon: Sparkles, title: "Generate names", text: "Our AI creates dozens of brandable, memorable name ideas." },
  { icon: Globe, title: "Check .com availability", text: "See instantly which domains are free to register." },
  { icon: TrendingUp, title: "See estimated value", text: "Discover which available .com domains could be worth holding." },
];

const FEATURES = [
  { icon: Sparkles, title: "AI-native naming", text: "Not a keyword shuffler — a commercial naming engine that scores brandability & investment potential." },
  { icon: Globe, title: "Live domain checks", text: "Real-time .com and multi-extension availability, so you never fall for a taken name." },
  { icon: TrendingUp, title: "Estimated domain value", text: "AI valuations help you spot .com names worth registering — and potentially holding." },
  { icon: Bookmark, title: "Save & compare", text: "Build a shortlist of favourites and revisit them any time across devices." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { track } = useAuth();
  const [idea, setIdea] = useState("");

  useEffect(() => {
    track("page_viewed", { page: "landing" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = () => {
    track("generator_entry", { from: "hero", has_idea: !!idea.trim() });
    navigate(idea.trim() ? `/generate?desc=${encodeURIComponent(idea.trim())}` : "/generate");
  };

  return (
    <div>
      <Seo
        title="Namable — AI Business Name Generator & Domain Finder"
        description="Describe your idea and get brandable business names with instant .com availability and estimated domain values. Find a name worth building."
        path="/"
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-70" />
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-brand/10 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-20 sm:pt-24 sm:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-surface border hairline px-3 py-1 text-[12px] font-semibold text-ink-muted shadow-soft">
                <Sparkles size={13} className="text-brand" /> AI names · live domains · value estimates
              </span>
              <h1 className="font-display text-[38px] sm:text-[54px] leading-[1.02] font-extrabold tracking-[-0.035em] text-ink mt-5">
                Find a name <span className="text-gradient">worth building.</span>
              </h1>
              <p className="text-[16px] sm:text-[18px] text-ink-muted mt-5 max-w-[480px] leading-relaxed">
                Namable is the AI naming studio for founders. Describe your idea, get brandable names, check .com availability and see estimated domain value — in seconds.
              </p>

              <div className="mt-8 card-soft p-2 flex flex-col sm:flex-row gap-2 max-w-[520px]">
                <input
                  value={idea}
                  onChange={(e) => setIdea(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && go()}
                  data-testid="hero-idea-input"
                  placeholder="An online gym clothing brand…"
                  className="flex-1 bg-transparent px-3.5 py-3 text-[15px] text-ink placeholder:text-ink-subtle outline-none"
                />
                <Button onClick={go} data-testid="hero-cta-btn" className="h-12 px-6 rounded-lg bg-brand hover:bg-brand-dark text-white font-bold btn-glow whitespace-nowrap">
                  Find My Name <ArrowRight size={16} className="ml-1.5" />
                </Button>
              </div>
              <div className="flex items-center gap-5 mt-5">
                <a href="#how" className="text-[14px] font-semibold text-ink-muted hover:text-ink transition-colors inline-flex items-center gap-1.5" data-testid="how-it-works-link">
                  See how it works
                </a>
                <span className="text-[13px] text-ink-subtle">First scan free — no signup</span>
              </div>
              <div className="mt-6" data-testid="hero-store-badges">
                <StoreBadges />
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="flex justify-center lg:justify-end">
              <HeroDemo />
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="text-center max-w-[560px] mx-auto">
          <p className="overline text-brand">How it works</p>
          <h2 className="font-display text-[28px] sm:text-[36px] font-extrabold tracking-[-0.02em] text-ink mt-2">
            From idea to available domain
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="card-soft p-5"
              >
                <div className="w-11 h-11 rounded-[12px] bg-brand-soft text-brand flex items-center justify-center mb-4">
                  <Icon size={20} />
                </div>
                <p className="text-[11px] font-bold text-ink-subtle">STEP {i + 1}</p>
                <h3 className="text-[16px] font-bold text-ink mt-1">{s.title}</h3>
                <p className="text-[13px] text-ink-muted mt-1.5 leading-relaxed">{s.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-surface border-y hairline">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <p className="overline text-brand">Why Namable</p>
              <h2 className="font-display text-[28px] sm:text-[36px] font-extrabold tracking-[-0.02em] text-ink mt-2">
                More than a name generator.
              </h2>
              <p className="text-[15px] text-ink-muted mt-4 leading-relaxed max-w-[440px]">
                Namable helps you find names people remember and domains you can actually own — with AI valuations that reveal hidden opportunity.
              </p>
              <Button onClick={() => navigate("/generate")} className="mt-7 h-12 px-6 rounded-xl bg-brand hover:bg-brand-dark text-white font-bold btn-glow" data-testid="features-cta">
                <Zap size={16} className="mr-2" /> Try it free
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {FEATURES.map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.title} className="card-soft p-5">
                    <Icon size={20} className="text-brand" />
                    <h3 className="text-[15px] font-bold text-ink mt-3">{f.title}</h3>
                    <p className="text-[13px] text-ink-muted mt-1.5 leading-relaxed">{f.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="relative overflow-hidden rounded-xxl brand-gradient px-6 sm:px-14 py-14 sm:py-20 text-center text-white shadow-glow">
          <div className="absolute inset-0 grid-bg opacity-20" />
          <div className="relative">
            <h2 className="font-display text-[30px] sm:text-[42px] font-extrabold tracking-[-0.02em]">
              Your next brand is one idea away.
            </h2>
            <p className="text-white/85 text-[16px] mt-4 max-w-[440px] mx-auto">
              Run your first scan free. No account, no credit card — just great names.
            </p>
            <Button
              onClick={() => navigate("/generate")}
              data-testid="bottom-cta-btn"
              className="mt-8 h-14 px-8 rounded-xl bg-white text-brand hover:bg-white/90 font-extrabold text-[16px]"
            >
              Find My Name <ArrowRight size={18} className="ml-2" />
            </Button>
            <div className="mt-8 pt-6 border-t border-white/15">
              <p className="text-white/70 text-[12px] font-semibold uppercase tracking-wide mb-3">Or take Namable mobile</p>
              <StoreBadges className="justify-center" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
